import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
} from "react-router";
import { LicenseInfo } from "@mui/x-license";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { clearInvalidLocalStoragePrefs } from "@pepsico/pref-permissions";

import {
  AppBaseView,
  AppFrame,
  Landing,
  Locations,
  Login,
  PageNotFound,
  PlotMap,
  RootErrorBoundary,
  UserAuthCheck,
  UserProvider,
  Users,
} from "components";
import { appPages, defaultPrefs, type AppPage } from "defs";

LicenseInfo.setLicenseKey(
  "61e6b6faf9be62d3494b41d8b2ea7ae9Tz0xMjA4NDcsRT0xNzkyNzk5OTk5MDAwLFM9cHJlbWl1bSxMTT1wZXJwZXR1YWwsUFY9aW5pdGlhbCxLVj0y",
);

const queryClient = new QueryClient();

const pageComponents: Record<
  AppPage,
  {
    Component?: React.ComponentType;
    children?: RouteObject[];
    loader?: RouteObject["loader"];
  }
> = {
  "": { Component: Landing },
  locations: { Component: Locations },
  map: { Component: PlotMap },
  users: { Component: Users },
};

const router = createBrowserRouter([
  {
    Component: UserProvider,
    children: [
      {
        Component: AppFrame,
        children: [
          {
            path: "login",
            Component: Login,
          },
          {
            Component: UserAuthCheck,
            children: [
              {
                Component: AppBaseView,
                children: [
                  ...Object.keys(appPages).map((path) => {
                    const props = pageComponents[path as AppPage];

                    return path
                      ? { path, ...props }
                      : ({ index: true, ...props } as RouteObject);
                  }),
                  { path: ":page", Component: PageNotFound },
                ],
              },
            ],
          },
        ],
      },
    ],
    ErrorBoundary: RootErrorBoundary,
  },
]);

clearInvalidLocalStoragePrefs(defaultPrefs);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
